import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { Check, DownloadSimple, Upload } from "@phosphor-icons/react";
import type { JsonResume, LinkedIn, ReactiveResumeV3 } from "@reactive-resume/parser";
import {
  JsonResumeParser,
  LinkedInParser,
  ReactiveResumeParser,
  ReactiveResumeV3Parser,
} from "@reactive-resume/parser";
import type { ResumeData } from "@reactive-resume/schema";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import { AnimatePresence } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z, ZodError } from "zod";

import { useToast } from "@/client/hooks/use-toast";
import { useImportResume } from "@/client/services/resume/import";
import { useParseResume } from "@/client/services/resume/parse";
import { useDialog } from "@/client/stores/dialog";

enum ImportType {
  "reactive-resume-json" = "reactive-resume-json",
  "reactive-resume-v3-json" = "reactive-resume-v3-json",
  "json-resume-json" = "json-resume-json",
  "linkedin-data-export-zip" = "linkedin-data-export-zip",
  "smart-parse" = "smart-parse",
}

const formSchema = z.object({
  file: z.instanceof(File).optional(),
  type: z.nativeEnum(ImportType),
});

type FormValues = z.infer<typeof formSchema>;

type ValidationResult =
  | {
      isValid: false;
      errors: string;
    }
  | {
      isValid: true;
      type: ImportType;
      result: ResumeData | ReactiveResumeV3 | LinkedIn | JsonResume;
    };

export const ImportDialog = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isOpen, close } = useDialog("import");
  const { importResume, loading } = useImportResume();
  const { parseResume, loading: parseLoading } = useParseResume();

  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isSmartParsing, setIsSmartParsing] = useState(false);

  const form = useForm<FormValues>({
    defaultValues: {
      type: ImportType["smart-parse"],
    },
    resolver: zodResolver(formSchema),
  });
  const filetype = form.watch("type");

  useEffect(() => {
    if (isOpen) onReset();
  }, [isOpen]);

  useEffect(() => {
    // Only reset validation result when filetype changes, preserve the file
    setValidationResult(null);
  }, [filetype]);

  const accept = useMemo(() => {
    // 移除文件类型限制，允许上传任何文件
    return "";
  }, [filetype]);

  const onValidate = async () => {
    console.log("22222");
    try {
      const { file, type } = formSchema.parse(form.getValues());

      // Check if file is provided
      if (!file) {
        toast({
          variant: "error",
          title: t`No file selected`,
          description: t`Please select a file to import`,
        });
        return;
      }

      if (type === ImportType["smart-parse"]) {
        setIsSmartParsing(true);
        try {
          const result = await parseResume(file);
          // 从解析结果中提取标题
          const title = result.basics?.name || file.name.replace(/\.[^/.]+$/, "") || "导入的简历";
          // 导入解析结果，使用提取的标题
          const importedResume = await importResume({ 
            data: result,
            title: title
          });
          
          toast({
            variant: "success",
            title: t`简历导入成功！`,
            description: t`您的简历已解析并导入`,
          });
          
          close();
          console.log("importedResume",importedResume);
          // 导航到编辑页面
          if (importedResume?.id) {
            navigate(`/builder/${importedResume.id}`);
          }
          
        } catch (error) {
          toast({
            variant: "error",
            title: t`简历解析失败`,
            description: error instanceof Error ? error.message : t`解析简历时发生未知错误`,
          });
        } finally {
          setIsSmartParsing(false);
        }
        return;
      }

      if (type === ImportType["reactive-resume-json"]) {
        const parser = new ReactiveResumeParser();
        const data = await parser.readFile(file);
        const result = parser.validate(data);

        setValidationResult({ isValid: true, type, result });
      }

      if (type === ImportType["reactive-resume-v3-json"]) {
        const parser = new ReactiveResumeV3Parser();
        const data = await parser.readFile(file);
        const result = parser.validate(data);

        setValidationResult({ isValid: true, type, result });
      }

      if (type === ImportType["json-resume-json"]) {
        const parser = new JsonResumeParser();
        const data = await parser.readFile(file);
        const result = parser.validate(data);

        setValidationResult({ isValid: true, type, result });
      }

      if (type === ImportType["linkedin-data-export-zip"]) {
        const parser = new LinkedInParser();
        const data = await parser.readFile(file);
        const result = await parser.validate(data);

        setValidationResult({ isValid: true, type, result });
      }
    } catch (error) {
      if (error instanceof ZodError) {
        setValidationResult({
          isValid: false,
          errors: error.toString(),
        });

        toast({
          variant: "error",
          title: t`An error occurred while validating the file.`,
        });
      }
    }
  };

  const onImport = async () => {
    const { type, file } = formSchema.parse(form.getValues());

    if (!validationResult?.isValid || validationResult.type !== type) return;

    try {
      let data: ResumeData | undefined;
      let title: string = "导入的简历";

      if (type === ImportType["reactive-resume-json"]) {
        const parser = new ReactiveResumeParser();
        data = parser.convert(validationResult.result as ResumeData);
        title = data?.basics?.name || file?.name.replace(/\.[^/.]+$/, "") || "导入的简历";
      }

      if (type === ImportType["reactive-resume-v3-json"]) {
        const parser = new ReactiveResumeV3Parser();
        data = parser.convert(validationResult.result as ReactiveResumeV3);
        title = data?.basics?.name || file?.name.replace(/\.[^/.]+$/, "") || "导入的简历";
      }

      if (type === ImportType["json-resume-json"]) {
        const parser = new JsonResumeParser();
        data = parser.convert(validationResult.result as JsonResume);
        title = data?.basics?.name || file?.name.replace(/\.[^/.]+$/, "") || "导入的简历";
      }

      if (type === ImportType["linkedin-data-export-zip"]) {
        const parser = new LinkedInParser();
        data = parser.convert(validationResult.result as LinkedIn);
        title = data?.basics?.name || file?.name.replace(/\.[^/.]+$/, "") || "导入的简历";
      }

      if (!data) {
        throw new Error("Failed to parse resume data");
      }

      await importResume({ data, title });

      close();
    } catch (error: unknown) {
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const onReset = () => {
    form.reset({
      file: undefined,
      type: ImportType["smart-parse"],
    });
    setValidationResult(null);
    setIsSmartParsing(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent>
        <Form {...form}>
          <form className="space-y-4">
            <DialogHeader>
              <DialogTitle>
                <div className="flex items-center space-x-2.5">
                  {filetype === ImportType["smart-parse"] ? <Upload /> : <DownloadSimple />}
                  <span>
                    {filetype === ImportType["smart-parse"] 
                      ? t`智能简历导入` 
                      : t`导入现有简历`
                    }
                  </span>
                </div>
              </DialogTitle>
              <DialogDescription>
                {filetype === ImportType["smart-parse"]
                  ? t`上传文件，我们将使用AI技术自动提取和解析您的简历内容。`
                  : t`从支持的来源上传文件，解析现有数据并导入到Reactive Resume中以便更轻松地编辑。`
                }
              </DialogDescription>
            </DialogHeader>
 {/* 
            <FormField
              name="type"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`导入方式`}</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder={t`请选择导入方式`} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="smart-parse">
                          🤖 智能解析 (PDF/DOC/DOCX)
                        </SelectItem>
                        <SelectItem value="reactive-resume-json">
                          Reactive Resume (.json)
                        </SelectItem>
                        <SelectItem value="reactive-resume-v3-json">
                          Reactive Resume v3 (.json)
                        </SelectItem>
                        <SelectItem value="json-resume-json">JSON Resume (.json)</SelectItem>
                        <SelectItem value="linkedin-data-export-zip">
                          LinkedIn 数据导出 (.zip)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            /> */}
            <FormField
              name="file"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`文件`}</FormLabel>
                  <FormControl>
                    <Input
                      key={`${accept}-${filetype}`}
                      type="file"
                      accept={accept}
                      onChange={(event) => {
                        if (!event.target.files?.length) return;
                        field.onChange(event.target.files[0]);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                  <FormDescription>
                    {t`支持上传任何类型的文件`}
                  </FormDescription>
                </FormItem>
              )}
            />

            {validationResult?.isValid === false && (
              <div className="space-y-2">
                <Label className="text-error">{t`Errors`}</Label>
                <ScrollArea orientation="vertical" className="h-[180px]">
                  <div className="whitespace-pre-wrap rounded bg-secondary-accent p-4 font-mono text-xs leading-relaxed">
                    {JSON.stringify(JSON.parse(validationResult.errors), null, 4)}
                  </div>
                </ScrollArea>
              </div>
            )}

            <DialogFooter>
              <AnimatePresence presenceAffectsLayout>
                {!validationResult && !isSmartParsing && (
                  <Button type="button" onClick={onValidate} disabled={parseLoading || loading}>
                    {filetype === ImportType["smart-parse"] 
                      ? (parseLoading ? t`解析中...` : t`解析并导入`)
                      : t`验证`
                    }
                  </Button>
                )}

                {isSmartParsing && (
                  <Button disabled type="button">
                    {t`处理中...`}
                  </Button>
                )}

                {validationResult !== null && !validationResult.isValid && (
                  <Button type="button" variant="secondary" onClick={onReset}>
                    {t`丢弃`}
                  </Button>
                )}

                {validationResult !== null && validationResult.isValid && (
                  <>
                    <Button type="button" disabled={loading} onClick={onImport}>
                      {t`导入`}
                    </Button>

                    <Button disabled type="button" variant="success">
                      <Check size={16} weight="bold" className="mr-2" />
                      {t`已验证`}
                    </Button>
                  </>
                )}
              </AnimatePresence>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
